import { detectDialect, VOICE_ID, voiceFor, voiceForLang } from "@qn/agent-core";
import { ApiError, api } from "./api";

export type VoiceLang = "auto" | "ar" | "en";

export interface VoiceSettings {
  /** Use OpenAI Realtime when the user has an OpenAI key (default on). */
  realtime?: boolean;
  stt: { source: "browser" | "workers-ai" | "provider"; providerId?: string; model?: string };
  tts: {
    source: "browser" | "provider";
    providerId?: string;
    model?: string;
    voice?: string;
    auto?: boolean;
    gender?: "female" | "male";
    /** Use Gemini TTS (free, natural dialects) when a Gemini key exists (default on). */
    gemini?: boolean;
  };
  lang: VoiceLang;
}
export const DEFAULT_VOICE: VoiceSettings = {
  stt: { source: "workers-ai" },
  tts: { source: "browser" },
  lang: "auto",
};

export const arabicRatio = (s: string) => {
  const letters = s.match(/\p{L}/gu) ?? [];
  return letters.length ? letters.filter((c) => /[؀-ۿ]/.test(c)).length / letters.length : 0;
};

/** Plain text for speech: no code, URLs, markdown noise. */
export function speakable(md: string): string {
  return md
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/!\[[^\]]*]\([^)]*\)/g, " ")
    .replace(/\[([^\]]+)]\([^)]*\)/g, "$1")
    .replace(/https?:\/\/\S+/g, " ")
    .replace(/^\s*[#>*\-+|]+\s*/gm, "")
    .replace(/[*_~|]+/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** Whole text → few longer chunks (sentence boundaries kept): neural voices sound far less choppy than reading one short sentence at a time. */
export function chunkForSpeech(md: string, max = 360): string[] {
  const clean = speakable(md.replace(/\p{Extended_Pictographic}/gu, " "));
  const sentences = clean.match(/[^.!?؟…\n]+[.!?؟…]*\s*/g) ?? [clean];
  const out: string[] = [];
  let cur = "";
  for (const s of sentences) {
    if (cur && cur.length + s.length > max) {
      out.push(cur.trim());
      cur = "";
    }
    cur += s;
  }
  if (cur.trim()) out.push(cur.trim());
  return out.filter((x) => x.length > 1);
}

/** Splits a growing text into complete sentences so speech can start before the answer is finished. */
export class SentenceStream {
  private buf = "";
  private first = true;
  push(delta: string): string[] {
    this.buf += delta;
    const out: string[] = [];
    // Latency: speak the first clause as soon as there is a natural pause (comma / colon) instead of waiting for a full sentence.
    if (this.first) {
      const m = /^([\s\S]{24,}?[,،:;؛])\s/.exec(this.buf);
      if (m) {
        const s = speakable(m[1] ?? "");
        this.buf = this.buf.slice(m[0].length);
        this.first = false;
        if (s.length > 1) out.push(s);
      }
    }
    // A sentence ends at . ! ? ؟ … or a newline followed by more text; keep chunks under ~220 chars.
    for (
      let m = /^([\s\S]*?[.!?؟…\n])(\s+|$)/.exec(this.buf);
      m;
      m = /^([\s\S]*?[.!?؟…\n])(\s+|$)/.exec(this.buf)
    ) {
      if (m[0].length === this.buf.length && !/\s$/.test(this.buf) && !/\n$/.test(m[1] ?? ""))
        break; // might still be mid-number ("3.")
      const s = speakable(m[1] ?? "");
      if (s.length > 1) out.push(s);
      this.buf = this.buf.slice(m[0].length);
    }
    while (this.buf.length > 260) {
      const cut = this.buf.lastIndexOf(" ", 220);
      const s = speakable(this.buf.slice(0, cut > 40 ? cut : 220));
      if (s) out.push(s);
      this.buf = this.buf.slice(cut > 40 ? cut : 220);
    }
    return out;
  }
  flush(): string[] {
    const s = speakable(this.buf);
    this.buf = "";
    return s ? [s] : [];
  }
}

// ---------------------------------------------------------------- recording

/** iOS (Safari and installed PWAs) has no usable SpeechRecognition while the mic is also open for recording. */
export const isIOS = () =>
  typeof navigator !== "undefined" &&
  (/iP(hone|ad|od)/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

export const micSupported = () =>
  typeof navigator !== "undefined" &&
  !!navigator.mediaDevices?.getUserMedia &&
  typeof MediaRecorder !== "undefined";

export function pickMime(): string {
  for (const m of ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/mp4"])
    if (MediaRecorder.isTypeSupported(m)) return m;
  return "";
}

export async function openMic(): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: 1,
    },
  });
}

export class Recorder {
  private rec?: MediaRecorder;
  private chunks: Blob[] = [];
  constructor(private stream: MediaStream) {}
  start() {
    this.chunks = [];
    const mime = pickMime();
    this.rec = new MediaRecorder(
      this.stream,
      mime ? { mimeType: mime, audioBitsPerSecond: 32_000 } : undefined,
    );
    this.rec.ondataavailable = (e) => e.data.size && this.chunks.push(e.data);
    this.rec.start(250);
  }
  stop(): Promise<Blob> {
    return new Promise((resolve) => {
      const r = this.rec;
      if (!r || r.state === "inactive")
        return resolve(new Blob(this.chunks, { type: r?.mimeType || "audio/webm" }));
      r.onstop = () => resolve(new Blob(this.chunks, { type: r.mimeType || "audio/webm" }));
      r.stop();
    });
  }
}

/** Energy-based voice activity detector with an adaptive noise floor. */
export class VAD {
  private ctx: AudioContext = new (
    window.AudioContext ??
    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
  )();
  private an: AnalyserNode;
  private timer?: ReturnType<typeof setInterval>;
  private floor = 0.008;
  private speaking = false;
  private loud = 0;
  private lastLoud = 0;
  /** Loudest level seen so far: stays ~0 when the browser hands us a dead microphone. */
  peak = 0;
  /** RMS multiplier above the noise floor that counts as speech (raise while the assistant is talking). */
  sensitivity = 3;
  onLevel?: (rms: number) => void;
  onStart?: () => void;
  onEnd?: () => void;
  constructor(
    stream: MediaStream,
    private silenceMs = 650,
  ) {
    const src = this.ctx.createMediaStreamSource(stream);
    this.an = this.ctx.createAnalyser();
    this.an.fftSize = 1024;
    src.connect(this.an);
  }
  run() {
    // iOS starts audio contexts suspended: without this the analyser reads silence forever.
    const wake = () => void this.ctx.resume().catch(() => undefined);
    wake();
    document.addEventListener("touchstart", wake, { once: true });
    document.addEventListener("click", wake, { once: true });
    const data = new Float32Array(this.an.fftSize);
    this.timer = setInterval(() => {
      this.an.getFloatTimeDomainData(data);
      let sum = 0;
      for (const v of data) sum += v * v;
      const rms = Math.sqrt(sum / data.length);
      this.onLevel?.(rms);
      const threshold = Math.max(0.012, this.floor * this.sensitivity);
      const now = Date.now();
      if (rms > this.peak) this.peak = rms;
      if (rms > threshold) {
        this.loud++;
        this.lastLoud = now;
        if (!this.speaking && this.loud >= 2) {
          this.speaking = true;
          this.onStart?.();
        }
      } else {
        this.loud = 0;
        if (!this.speaking)
          this.floor = this.floor * 0.95 + rms * 0.05; // learn the room's noise only while silent
        else if (now - this.lastLoud > this.silenceMs) {
          this.speaking = false;
          this.onEnd?.();
        }
      }
    }, 50);
  }
  get isSpeaking() {
    return this.speaking;
  }
  reset() {
    this.speaking = false;
    this.loud = 0;
  }
  close() {
    if (this.timer) clearInterval(this.timer);
    void this.ctx.close();
  }
}

// ---------------------------------------------------------------- speech-to-text

export async function transcribeServer(
  blob: Blob,
  lang: VoiceLang,
  uiLocale = "en",
): Promise<string> {
  const res = await fetch("/api/voice/transcribe", {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "content-type": blob.type || "audio/webm",
      "x-qn-client": "web",
      "x-ui-lang": uiLocale === "ar" ? "ar" : "en",
      ...(lang !== "auto" ? { "x-lang": lang } : {}),
    },
    body: blob,
  });
  if (!res.ok) {
    let m = `Transcription failed (${res.status})`;
    try {
      m = ((await res.json()) as { error?: { message?: string } }).error?.message ?? m;
    } catch {
      /* non-JSON */
    }
    throw new ApiError(res.status, m);
  }
  return ((await res.json()) as { text: string }).text;
}

type SR = {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult:
    | ((e: {
        results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>;
      }) => void)
    | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
};
export const browserSTT = (): (new () => SR) | null =>
  (
    window as unknown as {
      SpeechRecognition?: new () => SR;
      webkitSpeechRecognition?: new () => SR;
    }
  ).SpeechRecognition ??
  (window as unknown as { webkitSpeechRecognition?: new () => SR }).webkitSpeechRecognition ??
  null;
export const browserLang = (lang: VoiceLang, uiLocale: string) =>
  lang === "ar" || (lang === "auto" && uiLocale === "ar") ? "ar-EG" : "en-US";

// ---------------------------------------------------------------- text-to-speech

export interface Speaker {
  speak(text: string): void;
  cancel(): void;
  /** Resolves when everything queued has been spoken. */
  idle(): Promise<void>;
  readonly busy: boolean;
}

const FEMALE_HINT =
  /(salma|laila|leila|layla|zariyah|amany|sana|fatima|noura|amal|aysha|rana|mouna|reem|amina|iman|maryam|samantha|karen|moira|tessa|victoria|zira|jenny|sonia|female|woman)/i;
const LOCALES: Record<string, string[]> = {
  "ar-eg": ["ar-EG", "ar-SA", "ar"],
  "ar-sa": ["ar-SA", "ar-AE", "ar"],
  "ar-gulf": ["ar-AE", "ar-KW", "ar-QA", "ar-SA", "ar"],
  "ar-levant": ["ar-SY", "ar-LB", "ar-JO", "ar-SA", "ar"],
  "ar-maghreb": ["ar-MA", "ar-DZ", "ar-TN", "ar-SA", "ar"],
  "ar-fusha": ["ar-SA", "ar-EG", "ar"],
  en: ["en-US", "en-GB", "en"],
};
/** Locales to look for when a conversation has a known language (the assistant persona on a call). */
const LANG_LOCALES: Record<string, string[]> = {
  fr: ["fr-FR", "fr-CA", "fr"],
  es: ["es-ES", "es-MX", "es-US", "es"],
  de: ["de-DE", "de"],
  it: ["it-IT", "it"],
  pt: ["pt-BR", "pt-PT", "pt"],
  tr: ["tr-TR", "tr"],
  ru: ["ru-RU", "ru"],
  en: ["en-US", "en-GB", "en"],
};

/** Installed browser voices (what the device offers; Egyptian voices exist mainly on Android/Chrome and Windows). */
export function browserVoices(): SpeechSynthesisVoice[] {
  return typeof speechSynthesis === "undefined" ? [] : speechSynthesis.getVoices();
}

function pickVoice(
  text: string,
  cfg: VoiceSettings["tts"],
  hint?: string,
  lang?: string,
): SpeechSynthesisVoice | undefined {
  const voices = browserVoices();
  const code = lang?.slice(0, 2).toLowerCase();
  if (code && code !== "ar" && LANG_LOCALES[code]) {
    const female = cfg.gender !== "male";
    for (const w of LANG_LOCALES[code]!) {
      const pool = voices.filter(
        (x) => x.lang === w || x.lang.replace("_", "-").startsWith(`${w}-`),
      );
      if (pool.length) return pool.find((v) => FEMALE_HINT.test(v.name) === female) ?? pool[0];
    }
  }
  if (cfg.voice && !cfg.auto) {
    const chosen = voices.find((v) => v.voiceURI === cfg.voice);
    const isAr = arabicRatio(text) > 0.4;
    // Use the picked voice only when it can read this text (an Arabic sentence through a Danish voice is noise).
    if (
      chosen &&
      (chosen.lang.startsWith("ar") === isAr || (!isAr && chosen.lang.startsWith("en")))
    )
      return chosen;
  }
  const d = dialectOf(text, hint);
  const want = LOCALES[d ?? (arabicRatio(text) > 0.4 ? "ar-eg" : "en")] ?? LOCALES.en ?? [];
  for (const w of want) {
    const pool = voices.filter((x) => x.lang === w || x.lang.replace("_", "-").startsWith(`${w}-`));
    if (!pool.length) continue;
    const female = cfg.gender !== "male";
    return pool.find((v) => FEMALE_HINT.test(v.name) === female) ?? pool[0];
  }
  return voices.find((x) => x.lang.startsWith(arabicRatio(text) > 0.4 ? "ar" : "en")) ?? undefined;
}

/** iOS fills the voice list asynchronously: wait (briefly) for it before choosing. */
async function pickVoiceWhenReady(
  text: string,
  cfg: VoiceSettings["tts"],
  hint?: string,
  lang?: string,
): Promise<SpeechSynthesisVoice | undefined> {
  for (let i = 0; i < 10 && browserVoices().length === 0; i++)
    await new Promise((r) => setTimeout(r, 150));
  return pickVoice(text, cfg, hint, lang);
}

/** Dialect to speak a text in: Arabic text takes the dialect the user writes/speaks in (Saudi stays Saudi, Egyptian stays Egyptian). */
export function dialectOf(text: string, hint?: string) {
  const own = detectDialect([text]);
  if (arabicRatio(text) > 0.4 && hint) {
    const h = detectDialect([hint]);
    if (h?.startsWith("ar") && h !== "ar-fusha") return h;
  }
  return own;
}

export type SpeechIssue = "noVoice" | "noVoiceAr" | "silent";

// iOS only lets a page play audio from an element that was started inside a tap. Voice mode plays sentences long after the
// tap (after a fetch), so one element is "unlocked" by the tap that opens voice mode and then reused for every sentence.
const SILENT_WAV =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAIlYAAESsAAACABAAZGF0YQAAAAA=";
let sharedEl: HTMLAudioElement | null = null;
export const sharedAudio = (): HTMLAudioElement => {
  if (!sharedEl) {
    sharedEl = new Audio();
    sharedEl.setAttribute("playsinline", "");
  }
  return sharedEl;
};
/** Call from the tap/click that starts voice mode. */
export function unlockAudio() {
  try {
    const a = sharedAudio();
    a.src = SILENT_WAV;
    void a.play().catch(() => undefined);
    if ("speechSynthesis" in window) {
      const u = new SpeechSynthesisUtterance(" ");
      u.volume = 0;
      speechSynthesis.speak(u);
    }
  } catch {
    /* best effort */
  }
}

export function createSpeaker(
  cfg: VoiceSettings["tts"],
  onIssue?: (issue: SpeechIssue) => void,
  /** Recent text written/spoken by the user: decides the Arabic dialect of the voice. */
  dialectHint?: string,
  /** Conversation language (assistant persona): a non-Arabic value picks that language's voice instead of guessing from the text. */
  lang?: string,
): Speaker {
  const queue: string[] = [];
  let playing = false;
  let cancelled = 0;
  let current: HTMLAudioElement | null = null;
  const waiters: Array<() => void> = [];
  const done = () => {
    while (waiters.length) waiters.shift()?.();
  };

  /** Catalog voice for this sentence: matched to its dialect in auto mode, else the one the user picked. */
  const serverVoice = (text: string): string | undefined => {
    if (lang && lang.slice(0, 2) !== "ar") return voiceForLang(lang, cfg.gender ?? "female")?.id;
    if (cfg.auto) return voiceFor(dialectOf(text, dialectHint), cfg.gender ?? "female")?.id;
    return cfg.voice && VOICE_ID.test(cfg.voice) ? cfg.voice : undefined;
  };

  async function browserSpeak(text: string) {
    if (!("speechSynthesis" in window)) return void onIssue?.("silent");
    const v = await pickVoiceWhenReady(text, cfg, dialectHint, lang);
    // A language with no installed voice is silent on iOS/Android: say so instead of failing quietly.
    if (!v) return void onIssue?.(arabicRatio(text) > 0.4 ? "noVoiceAr" : "noVoice");
    await new Promise<void>((resolve) => {
      const u = new SpeechSynthesisUtterance(text);
      u.voice = v;
      u.lang = v.lang;
      let started = false;
      // If nothing starts within 3 s the device is muted/blocked (silent switch, volume, autoplay rules).
      const dog = setTimeout(() => {
        if (started) return;
        speechSynthesis.cancel();
        onIssue?.("silent");
        resolve();
      }, 3000);
      u.onstart = () => {
        started = true;
        clearTimeout(dog);
      };
      u.onend = () => {
        clearTimeout(dog);
        resolve();
      };
      u.onerror = () => {
        clearTimeout(dog);
        resolve();
      };
      speechSynthesis.resume();
      speechSynthesis.speak(u);
    });
  }

  async function playOne(text: string, epoch: number) {
    if (cfg.source === "provider") {
      let blob: Blob | null = null;
      try {
        const res = await fetch("/api/voice/speak", {
          method: "POST",
          credentials: "same-origin",
          headers: { "content-type": "application/json", "x-qn-client": "web" },
          body: JSON.stringify({
            text,
            ...(serverVoice(text) ? { voice: serverVoice(text) } : {}),
          }),
        });
        if (res.ok) blob = await res.blob();
      } catch {
        /* network: fall back below */
      }
      if (epoch !== cancelled) return;
      // The server voice failed (key, quota, network): speak with the device voice rather than stay silent.
      if (!blob || blob.size < 100) return browserSpeak(text);
      const url = URL.createObjectURL(blob);
      const ok = await new Promise<boolean>((resolve) => {
        const a = sharedAudio();
        current = a;
        const end = (played: boolean) => {
          a.onended = null;
          a.onerror = null;
          URL.revokeObjectURL(url);
          current = null;
          resolve(played);
        };
        a.onended = () => end(true);
        a.onerror = () => end(false);
        a.src = url;
        a.play().catch(() => end(false));
      });
      if (!ok && epoch === cancelled) await browserSpeak(text);
    } else {
      await browserSpeak(text);
    }
  }

  async function pump() {
    if (playing) return;
    playing = true;
    const epoch = cancelled;
    while (queue.length && epoch === cancelled) {
      const t = queue.shift() as string;
      try {
        await playOne(t, epoch);
      } catch {
        /* skip a failed sentence, keep talking */
      }
    }
    playing = false;
    if (!queue.length) done();
  }

  return {
    speak(t) {
      if (t.trim()) {
        queue.push(t);
        void pump();
      }
    },
    cancel() {
      cancelled++;
      queue.length = 0;
      current?.pause();
      current = null;
      if ("speechSynthesis" in window) speechSynthesis.cancel();
      playing = false;
      done();
    },
    idle: () =>
      playing || queue.length ? new Promise<void>((r) => waiters.push(r)) : Promise.resolve(),
    get busy() {
      return playing || queue.length > 0;
    },
  };
}

export async function fetchVoiceCaps() {
  return api<{
    /** An OpenAI key exists, so live (full-duplex) voice can run. */
    realtime: boolean;
    workersAi: boolean;
    runnerOnline: boolean;
    providers: Array<{ id: string; name: string; baseUrl: string; speechOnly?: boolean }>;
  }>("/voice/capabilities");
}

/** One-utterance listener on the browser's SpeechRecognition: start() when speech begins, stop() resolves with the text. */
export class BrowserListener {
  private r: InstanceType<NonNullable<ReturnType<typeof browserSTT>>> | null = null;
  private text = "";
  private ended: Promise<void> = Promise.resolve();
  start(lang: string) {
    const Ctor = browserSTT();
    if (!Ctor) return false;
    const r = new Ctor();
    r.lang = lang;
    r.continuous = true;
    r.interimResults = false;
    this.text = "";
    r.onresult = (e) => {
      this.text = Array.from(e.results)
        .map((x) => x[0]?.transcript ?? "")
        .join(" ");
    };
    this.ended = new Promise<void>((res) => {
      r.onend = () => res();
      r.onerror = () => res();
    });
    this.r = r;
    try {
      r.start();
    } catch {
      return false;
    }
    return true;
  }
  async stop(): Promise<string> {
    const r = this.r;
    this.r = null;
    if (!r) return "";
    try {
      r.stop();
    } catch {
      /* already stopped */
    }
    await Promise.race([this.ended, new Promise((x) => setTimeout(x, 2500))]);
    return this.text.trim();
  }
  abort() {
    try {
      this.r?.abort();
    } catch {
      /* noop */
    }
    this.r = null;
  }
}
